import React from 'react';
import { Link } from 'react-router-dom';
import { Trophy, TrendingUp, Users, ArrowRight } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';

const ClubsLanding = () => {
  const clubs = [
    {
      icon: Trophy,
      title: 'Be Immortal',
      subtitle: 'Life Skills & Leadership',
      description: 'Build the mindset, discipline, and character required to thrive in school, sports, and life. Develop confidence, communication skills, and leadership qualities that last a lifetime.',
      link: '/clubs/be-immortal',
      features: [
        'Leadership Training',
        'Character Development',
        'Life Readiness Skills',
        'Empowerment Workshops'
      ]
    },
    {
      icon: TrendingUp,
      title: 'The Mogul Manifest',
      subtitle: 'Entrepreneurship & Innovation',
      description: 'Turn ideas into action, solve real problems, and build opportunities for yourself and your community. Learn entrepreneurial thinking, creativity, and hands-on business experience.',
      link: '/clubs/mogul-manifest',
      features: [
        'Entrepreneurship Workshops',
        'Innovation Labs',
        'Real Money Projects',
        'Mentorship from Entrepreneurs'
      ]
    },
    {
      icon: Users,
      title: 'The Prosperity Project',
      subtitle: 'Financial Literacy & Wealth Building',
      description: 'Learn the financial skills schools don\'t teach. Master money management, investing basics, and modern wealth-building strategies to take control of your financial future.',
      link: '/clubs/prosperity-project',
      features: [
        'Money Foundations',
        'Wealth Building Basics',
        'Modern Money Skills',
        'Hands-On Learning'
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-black text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#E6B933] rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#E6B933] rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="inline-block mb-6 px-6 py-2 bg-[#E6B933]/20 rounded-full border border-[#E6B933]">
              <p className="text-[#E6B933] font-semibold">Specialized Development Programs</p>
            </div>
            <h1 className="text-6xl md:text-7xl font-bold mb-6">
              Extracurricular <span className="text-[#E6B933]">Clubs</span>
            </h1>
            <p className="text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Choose your path: Leadership, Entrepreneurship, or Financial Literacy
            </p>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Build Your <span className="text-[#E6B933]">Future</span>
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              Our specialized clubs provide focused development in critical life areas. Whether you want to become a leader, an entrepreneur, or a financial expert, we have a program designed to help you excel.
            </p>
          </div>
        </div>
      </section>

      {/* Clubs Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {clubs.map((club, index) => {
              const IconComponent = club.icon;
              return (
                <Card key={index} className="p-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-t-4 border-[#E6B933] group">
                  <div className="w-16 h-16 bg-[#E6B933]/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#E6B933] transition-colors duration-300">
                    <IconComponent className="text-[#E6B933] group-hover:text-white transition-colors duration-300" size={32} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{club.title}</h3>
                  <p className="text-[#E6B933] font-semibold mb-4">{club.subtitle}</p>
                  <p className="text-gray-600 mb-6 leading-relaxed">{club.description}</p>
                  
                  <div className="space-y-2 mb-6">
                    {club.features.map((feature, fIndex) => (
                      <div key={fIndex} className="flex items-center space-x-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#E6B933]"></div>
                        <p className="text-sm text-gray-600">{feature}</p>
                      </div>
                    ))}
                  </div>
                  
                  <Link to={club.link}>
                    <Button className="w-full bg-black hover:bg-gray-800 text-white font-semibold py-3 transition-all duration-300 group">
                      Learn More
                      <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" size={18} />
                    </Button>
                  </Link>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who Should Join */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="p-12 bg-gradient-to-br from-gray-50 to-white border-2 border-[#E6B933]">
            <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center">
              Who Should <span className="text-[#E6B933]">Join?</span>
            </h2>
            <p className="text-xl text-gray-600 text-center leading-relaxed">
              Middle school and high school students ready to grow, lead, and elevate their future through specialized development programs.
            </p>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#E6B933] rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-5xl font-bold mb-6">
            Ready to <span className="text-[#E6B933]">Get Started?</span>
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Choose your club above and begin your journey toward excellence.
          </p>
        </div>
      </section>
    </div>
  );
};

export default ClubsLanding;
